'use client';

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center text-sm font-semibold transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background transform hover:scale-[1.02]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-neumorphic',
        secondary: 'bg-secondary text-primary-foreground hover:bg-secondary/90 shadow-neumorphic',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-neumorphic',
        'subtle-primary': 'bg-muted text-primary hover:bg-muted/80 shadow-neumorphic',
        'subtle-secondary': 'bg-muted text-secondary hover:bg-muted/80 shadow-neumorphic',
        'light-primary': 'bg-background text-primary shadow-neumorphic hover:shadow-neumorphic-lg',
        'light-secondary': 'bg-background text-secondary shadow-neumorphic hover:shadow-neumorphic-lg',
        'outline-primary': 'border border-border bg-transparent text-primary hover:bg-primary hover:text-primary-foreground',
        'outline-secondary': 'border border-border bg-transparent text-secondary hover:bg-secondary hover:text-primary-foreground',
        outline: 'border border-border bg-transparent text-primary hover:bg-primary hover:text-primary-foreground',
        ghost: 'bg-transparent text-primary hover:bg-primary/10 hover:text-primary',
        'ghost-secondary': 'bg-transparent text-secondary hover:bg-secondary/10 hover:text-secondary',
        link: 'underline-offset-4 hover:underline text-primary',
      },
      size: {
        default: 'text-base h-auto py-3 px-6 rounded-xl',
        sm: 'text-sm h-auto py-2 px-4 rounded-xl',
        lg: 'text-lg h-auto py-4 px-8 rounded-xl',
        icon: 'h-10 w-10 rounded-xl shadow-neumorphic',
      },
      shadow: {
        true: '',
        false: '',
      },
      bordered: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { variant: ['default', 'secondary', 'destructive', 'subtle-primary', 'subtle-secondary', 'light-primary', 'light-secondary'], shadow: false, className: 'shadow-none' },
      { size: 'icon', shadow: false, className: 'shadow-none' },
      { variant: ['outline-primary', 'outline-secondary', 'outline', 'ghost', 'ghost-secondary', 'link'], shadow: true, className: 'shadow-neumorphic' },
      { variant: ['outline-primary', 'outline-secondary', 'outline'], bordered: false, className: 'border-none' },
      { variant: ['default', 'secondary', 'destructive', 'subtle-primary', 'subtle-secondary', 'light-primary', 'light-secondary', 'ghost', 'ghost-secondary', 'link'], bordered: true, className: 'border border-border' },
      { size: 'icon', bordered: true, className: 'border border-border' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  shadow?: boolean;
  bordered?: boolean;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((
  {
    className,
    variant,
    size,
    shadow,
    bordered,
    asChild = false,
    ...props
  },
  ref
) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, shadow, bordered, className }))}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = 'Button'

export { Button, buttonVariants } 